import {useCallback, useMemo} from 'react';
import _ from 'underscore';
// import {InfiniteQueryObserverResult as LegacyInfiniteQueryObserverResult} from 'react-query';
import {
	type InfiniteQueryObserverResult,
	useQueryClient,
	type InfiniteData,
	type QueryKey,
	type UseQueryResult,
} from '@tanstack/react-query';
import {debouncePromise} from '@netx/core-next/utils/promise';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';

// TODO: I have made sins in here just to make some stuff work
// probably better to break this out as helpers for infinite data and helpers for plain query data

export const useReactQueryUtils = <
	TResult,
	TError,
	TData extends React.MutableRefObject<any>,
>(
	reactQuery:
		| InfiniteQueryObserverResult<TResult, TError>
		//| LegacyInfiniteQueryObserverResult<TResult, TError>
		| UseQueryResult<TResult, TError>,
	dataRef: TData,
	idKey = 'id',
	dataReady: null | boolean = null,
	disabled = false,
) => {
	const queryClient = useQueryClient();

	const invalidate = useConstCallback((key: QueryKey | string) => {
		dataRef.current = [];
		if (disabled) return;
		queryClient.invalidateQueries(
			typeof key !== 'string'
				? {queryKey: key}
				: {
						predicate: ({queryKey}) => queryKey[0] === key,
						refetchPage: (lastPage, index, allPages) => {
							return true;
						},
					},
		);
	});

	const loadMoreItems = useConstCallback(
		async (startIndex: number, stopIndex: number) => {
			if (disabled) return;
			const canFetchMore =
				(dataReady == null && !!dataRef.current.length) || dataReady === true;
			// console.log('startIndex', startIndex, 'stopIndex', stopIndex);
			if (!(reactQuery as any).fetchNextPage || !canFetchMore) return;

			for (let i = startIndex, c = stopIndex + 1; i < c; i++) {
				if (dataRef.current[i] != null) startIndex++;
			}

			if (startIndex > stopIndex) return undefined;

			return await (
				reactQuery as InfiniteQueryObserverResult<TResult, TError>
			).fetchNextPage({
				pageParam: {
					startIndex: startIndex,
					stopIndex: stopIndex + 1,
				},
			});
		},
		// [
		// 	(reactQuery as InfiniteQueryObserverResult<TResult, TError>)
		// 		.fetchNextPage,
		// ],
	);

	/** Check if item has been loaded */
	const isItemLoaded = useCallback(
		(index: number) => dataRef.current[index] != null,
		[],
	);

	/** Debounced load more items */
	const debouncedLoadMoreItems = useMemo(
		() => debouncePromise(loadMoreItems, 300),
		[loadMoreItems],
	);

	const debouncedRefetch = useMemo(() => {
		if (disabled) return () => {};
		return _.debounce(reactQuery.refetch, 300);
	}, [reactQuery.refetch, disabled]);

	const removeItemFromCache = useCallback((key: QueryKey | string, id: any) => {
		let itemRemoved = false;
		const update = (data: InfiniteData<{results: object[]}> | object[]) => {
			if (!data) return data;
			if ((data as InfiniteData<{results: object[]}>).pages) {
				(data as InfiniteData<{results: object[]}>).pages = (
					data as InfiniteData<{results: object[]}>
				).pages.map((page) => {
					const lengthBefore = page.results.length;
					page.results = page.results.filter(
						(result) => (result as any)[idKey] !== id,
					);
					if (page.results.length !== lengthBefore) itemRemoved = true;
					return page;
				});
				if (itemRemoved) {
					// TODO: gross, obvs
					if (
						(
							(data as InfiniteData<{results: object[]}>).pages[
								(data as InfiniteData<{results: object[]}>).pages.length - 1
							] as any
						).size
					) {
						(
							(data as InfiniteData<{results: object[]}>).pages[
								(data as InfiniteData<{results: object[]}>).pages.length - 1
							] as any
						).size--;
					}
				}
			} else {
				(data as object[]) = (data as object[]).filter((d) => {
					return (d as any)[idKey] !== id;
				});
			}
			return data;
		};

		if (typeof key !== 'string') {
			queryClient.setQueryData(key, update as any);
			return;
		}

		// @ts-ignore
		queryClient.setQueriesData(
			{
				predicate: ({queryKey}: any) => queryKey[0] === key,
				updater: update,
			},
			update,
		);
	}, []);

	const updateItemInCache = useCallback(
		(key: QueryKey | string, updatedData: any) => {
			const update = (data: InfiniteData<{results: object[]}> | object[]) => {
				if (!data) return data;
				if ((data as InfiniteData<{results: object[]}>).pages) {
					(data as InfiniteData<{results: object[]}>).pages = (
						data as InfiniteData<{results: object[]}>
					).pages.map((page) => {
						page.results = page.results.map((result) => {
							if ((result as any)[idKey] !== (updatedData as any)[idKey])
								return result;
							return {
								...result,
								...updatedData,
							};
						});
						return page;
					});
				} else {
					(data as object[]) = (data as object[]).map((d) => {
						if ((d as any)[idKey] !== (updatedData as any)[idKey]) return d;
						return {
							...d,
							...updatedData,
						};
					});
				}
				return data;
			};

			if (typeof key !== 'string') {
				queryClient.setQueryData(key, update as any);
				return;
			}

			// @ts-ignore
			queryClient.setQueriesData(
				{
					predicate: ({queryKey}: any) => queryKey[0] === key,
					updater: update,
				},
				update,
			);
		},
		[],
	);

	const handleDeleteMutationSuccess = useCallback(
		({queryKey, cb}: {queryKey: QueryKey | string; cb?: () => void}) =>
			(id: any | any[]) => {
				if (!Array.isArray(id)) id = [id];
				// console.log(queryKey, id);
				id.forEach((id: any) => removeItemFromCache(queryKey, id));
				// invalidate(queryKey);
				cb?.();
			},
		[],
	);

	const handleEditMutationSuccess = useCallback(
		<T extends object = object>(params: {
			queryKey: QueryKey | string;
			cb?: () => void;
			editEffectsOrder?: (params: {originalData: T; updatedData: T}) => boolean;
			editRemovesFromFilter?: (params: {
				originalData: T;
				updatedData: T;
			}) => boolean;
		}) =>
			(updatedData: T) => {
				const {queryKey, cb, editEffectsOrder, editRemovesFromFilter} = params;

				const existing = dataRef.current.find(
					(d: any) => (d as any)?.[idKey] === (updatedData as any)[idKey],
				);

				// new
				if (!existing) {
					invalidate(queryKey);
					cb?.();
					return;
				}

				// no longer pertinent to the filters
				if (
					editRemovesFromFilter?.({
						originalData: existing,
						updatedData: updatedData,
					})
				) {
					removeItemFromCache(queryKey, (updatedData as any)[idKey]);
					invalidate(queryKey);
					cb?.();
					return;
				}

				// preemptive update
				updateItemInCache(queryKey, updatedData);

				// may not be in the correct order any longer
				if (
					editEffectsOrder?.({
						originalData: existing,
						updatedData: updatedData,
					})
				)
					invalidate(queryKey);

				cb?.();
			},
		[],
	);

	const loading = reactQuery.isFetching || reactQuery.isLoading;

	return {
		loading,
		debouncedLoadMoreItems,
		debouncedRefetch,
		handleDeleteMutationSuccess,
		handleEditMutationSuccess,
		invalidate,
		isItemLoaded,
		loadMoreItems,
		removeItemFromCache,
		updateItemInCache,
	};
};

export default useReactQueryUtils;
